// /////////////////////////////////////////////////////////////
// [ファイル] UnitPanel.tsx
// [概要] 案件詳細「販売区画」タブ（推進案件機能統合 リリース1）
//        区画（顧客 複数可・受注予定年月・受注確定日・フェーズ・売上）の一覧と編集、
//        フェーズ変更（受注は確定日必須／失注は理由必須）。計上月・予定超過・配分原価は
//        バックエンドの計算値を表示するだけ。仕入のない区画 1 つの案件では「販売情報」として直接表示する。
//        追加・削除の操作は、詳細画面の編集モードのときだけ出す（閲覧中は表示だけ）。
//        フェーズも編集モードでチップの横に鉛筆が出て、浮いた入力欄で変える（必要な値の欄だけが増える）。
//        区画の内容（区画名・顧客・種別・予定年月・確定日・金額・仲介）は、編集モードで項目ごとにその場で直す
//        （ダイアログを開かない）。区画が複数の案件は、表で区画を選ぶと下にその区画の詳細が出る。
//        新しい区画の追加はダイアログで行う。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Radio,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import LoadingView from '../LoadingView';
import { InlineAmountField, InlineEdit, InlineEditorArgs, InlineSelectField, InlineTextField, useCanEdit } from '../InlineEditField';
import CustomerPicker, { CustomerOption } from './CustomerPicker';
import { InlineCustomerField, InlineUnitCustomersField, useLostReasons } from './SfaInlineFields';
import { BookingChip, OverdueChip, UnitPhaseChip } from './SfaStructureChips';
import { AnkenChips, AnkenNumberPicker, InlineAnkenField } from './AnkenNumberPicker';
import {
  SfaProjectDetail, SfaUnit, SfaUnitCustomer, sfaStructureApi, customerLabel, fmtDate, fmtShare, fmtYen, fmtYm, parseShare, IDLE_PHASE, LOST_PHASE, ORDER_PHASE, UNIT_KINDS, SELECTABLE_PHASES,
  normalizeSystemNumbers, SYSTEM_NUMBER_LABEL,
} from '../../hooks/useSfaStructure';

// 列が多い表は折り返さず横スクロールさせる（見出し・セルの縦折り返しを防ぐ）
const NOWRAP_TABLE_SX = { '& th, & td': { whiteSpace: 'nowrap' } };

const dialogTitleSx = { fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 1 };

interface Props {
  detail: SfaProjectDetail;
  flat: boolean; // 仕入が無く区画 1 つ → 区画を意識させず販売情報として表示
  operatorId?: string;
  onChanged: () => void | Promise<void>;
}

const bookingCell = (u: SfaUnit) => {
  if (u.unit_state === 'idle') return <Typography variant="caption" color="text.secondary">販売前（集計対象外）</Typography>;
  if (u.unit_state === 'lost') return <Typography variant="caption" color="text.secondary">失注</Typography>;
  return <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><BookingChip ym={u.booking_ym} confirmed={!!u.order_confirmed_date} /><OverdueChip show={u.is_overdue} /></Box>;
};

export default function UnitPanel({ detail, flat, operatorId, onChanged }: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const projectId = detail.project.project_id as string;
  const [addOpen, setAddOpen] = useState(false);
  // 区画が複数の案件で、表の下に詳細を出す区画（行を押して選ぶ）
  const [selectedId, setSelectedId] = useState<number | null>(detail.units[0]?.id ?? null);
  const alloc = (id: number) => detail.allocation.find((a) => a.unit_id === id);
  const canEdit = useCanEdit();

  useEffect(() => {
    if (!detail.units.some((u) => u.id === selectedId)) setSelectedId(detail.units[0]?.id ?? null);
  }, [detail.units, selectedId]);

  const deleteUnit = async (u: SfaUnit) => {
    const ok = await confirm({ title: '販売区画の削除', message: `「${u.unit_name}」を削除しますか？（フェーズ履歴がある区画は削除できません。失注にしてください）`, confirmText: '削除する', destructive: true });
    if (!ok) return;
    try { await sfaStructureApi.deleteUnit(projectId, u.id); notify.success('削除しました'); onChanged(); } catch (e: any) { notify.error(e.message); }
  };

  if (flat) {
    const u = detail.units[0];
    return (
      // 説明は右の「案件の構成」にあるため、ここには置かない（同じ文を 2 か所に出さない）
      <Box>
        <UnitFields unit={u} projectId={projectId} operatorId={operatorId} onChanged={onChanged} flat />
      </Box>
    );
  }

  const selected = detail.units.find((u) => u.id === selectedId) || null;
  return (
    <Box>
      {/* 区画の決まり（計上月・集計対象・原価配分）の説明はマニュアルに任せ、画面には操作の手がかりだけを表の下に小さく置く */}
      <Box sx={{ overflowX: 'auto' }}><Table size="small" sx={NOWRAP_TABLE_SX}>
        <TableHead>
          <TableRow>
            <TableCell>区画</TableCell><TableCell>フェーズ</TableCell><TableCell>顧客</TableCell><TableCell>受注予定年月</TableCell><TableCell>受注確定日</TableCell>
            <TableCell align="right">売上</TableCell><TableCell>割当筆</TableCell><TableCell align="right">配分原価（自動）</TableCell><TableCell align="right">粗利（概算）</TableCell><TableCell>業績計上</TableCell>{canEdit && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.units.map((u) => (
            <TableRow key={u.id} hover selected={u.id === selectedId} onClick={() => setSelectedId(u.id)} sx={{ cursor: 'pointer' }}>
              <TableCell>
                <strong>{u.unit_name}</strong><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{u.unit_kind}</Typography>
                {!!u.system_numbers?.length && <Box sx={{ mt: 0.25 }}><AnkenChips numbers={u.system_numbers} links={u.anken_links} /></Box>}
              </TableCell>
              <TableCell>
                {/* 編集モードではチップの横に鉛筆が出て、その場でフェーズを変える */}
                <InlineUnitPhaseField unit={u} projectId={projectId} operatorId={operatorId} onChanged={onChanged} />
              </TableCell>
              <TableCell>{u.customers.length ? u.customers.map(customerLabel).join('、') : <Typography variant="caption" color="text.secondary">未定</Typography>}</TableCell>
              <TableCell>{fmtYm(u.expected_order_ym)}</TableCell>
              <TableCell>{fmtDate(u.order_confirmed_date)}</TableCell>
              <TableCell align="right">{u.sales_amount ? Number(u.sales_amount).toLocaleString() : '-'}</TableCell>
              <TableCell>{u.parcel_chiban || (u.unit_state === 'idle' ? '-' : <Chip size="small" label="未割当" sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#ffebee', color: '#c62828' }} />)}</TableCell>
              <TableCell align="right">{alloc(u.id)?.allocated_cost ? alloc(u.id)!.allocated_cost.toLocaleString() : '-'}</TableCell>
              <TableCell align="right">{u.unit_state === 'idle' ? '-' : (alloc(u.id)?.gross_profit ?? 0).toLocaleString()}</TableCell>
              <TableCell>{bookingCell(u)}</TableCell>
              {canEdit && (
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteUnit(u); }} disabled={detail.units.length <= 1} aria-label="区画を削除"><Icons.Delete fontSize="small" /></IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table></Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        {canEdit && <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_CTA_BUTTON_SX} onClick={() => setAddOpen(true)}>区画を追加</Button>}
        <Typography variant="caption" color="text.secondary">区画の行を押すと、下にその区画の詳細が出ます。</Typography>
      </Box>
      {/* 選んだ区画の詳細。編集モードでは項目ごとにその場で直す（ダイアログは開かない） */}
      {selected && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mt: 1.5, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
            <Typography variant="subtitle2">{selected.unit_name} の詳細</Typography>
            <InlineUnitPhaseField unit={selected} projectId={projectId} operatorId={operatorId} onChanged={onChanged} />
          </Box>
          <UnitFields key={selected.id} unit={selected} projectId={projectId} operatorId={operatorId} onChanged={onChanged} flat={false} />
        </Box>
      )}
      <UnitDialog open={addOpen} unit={null} projectId={projectId} flat={false} operatorId={operatorId} onClose={() => setAddOpen(false)} onSaved={onChanged} />
    </Box>
  );
}

// --------------------------------------------------
// 区画の内容。編集モードでは項目ごとにその場で直す（送った項目だけ更新）
// --------------------------------------------------
// 販売前の区画にこれらを入れたら、販売を始めたとみなして 1.見込み へ進める（区画の追加ダイアログと同じ扱い）
const SALE_KEYS = ['sales_amount', 'expected_order_ym', 'order_confirmed_date', 'customers'];

function UnitFields({ unit: u, projectId, operatorId, onChanged, flat }: {
  unit: SfaUnit; projectId: string; operatorId?: string; onChanged: () => void | Promise<void>; flat: boolean;
}) {
  const notify = useNotification();
  // 失敗は通知し、入力欄に理由を残す（例外を投げ直す）。再読込を待ってから入力欄を閉じる
  const save = async (patch: Record<string, any>, label: string) => {
    const advance = u.phase === IDLE_PHASE && Object.entries(patch).some(([k, v]) => SALE_KEYS.includes(k) && (Array.isArray(v) ? v.length > 0 : !!v));
    try {
      await sfaStructureApi.upsertUnit(projectId, { id: u.id, ...patch, ...(advance ? { phase: '1.見込み' } : {}) }, operatorId);
    } catch (e: any) {
      notify.error(e.message);
      throw e;
    }
    notify.success(advance ? `${label}を更新しました（販売を始めたため、フェーズを 1.見込み にしました）` : `${label}を更新しました`);
    await onChanged();
  };
  const yen = (v: any, strong?: boolean) => <Typography component="span" variant="body2" sx={{ fontWeight: strong ? 600 : 400 }}>{fmtYen(v)}</Typography>;
  const amount = (key: 'sales_amount' | 'construction_cost' | 'brokerage_fee_in' | 'brokerage_fee_out', label: string, strong?: boolean) => (
    <Field label={label} strong={strong} value={<InlineAmountField value={u[key]} label={label} display={yen(u[key], strong)} onSave={(v) => save({ [key]: v }, label)} />} />
  );
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
      {!flat && <Field label="区画名" value={<InlineTextField value={u.unit_name} label="区画名" required onSave={(v) => save({ unit_name: v }, '区画名')} />} />}
      <Field label="顧客（複数可）" value={<InlineUnitCustomersField customers={u.customers} onSave={(c) => save({ customers: c }, '顧客')} />} />
      <Field label="種別" value={<InlineSelectField value={u.unit_kind} options={UNIT_KINDS.map((k) => ({ value: k, label: k }))} label="種別" onSave={(v) => save({ unit_kind: v }, '種別')} />} />
      {/* 案件番号 (Hozenall NET が採番し工事管理へ引き継ぐ統一コード)。ミラーから検索して選ぶ。区画ごとに複数持てる */}
      <Field label={SYSTEM_NUMBER_LABEL} value={
        <InlineAnkenField value={u.system_numbers} links={u.anken_links} onSave={(v) => save({ system_numbers: v }, '案件番号')} />
      } />
      {flat && <Field label="フェーズ" value={<InlineUnitPhaseField unit={u} projectId={projectId} operatorId={operatorId} onChanged={onChanged} />} />}
      <Field label="受注予定年月" value={
        <InlineTextField type="month" value={u.expected_order_ym} label="受注予定年月" display={<Typography component="span" variant="body2">{fmtYm(u.expected_order_ym)}</Typography>}
          onSave={(v) => save({ expected_order_ym: v }, '受注予定年月')} />
      } />
      <Field label="受注確定日" strong value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InlineTextField type="date" value={u.order_confirmed_date} label="受注確定日" display={<Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(u.order_confirmed_date)}</Typography>}
            onSave={(v) => save({ order_confirmed_date: v }, '受注確定日')} />
          <OverdueChip show={u.is_overdue} />
        </Box>
      } />
      <Field label="業績見込み 計上月" value={bookingCell(u)} />
      {amount('sales_amount', '売上', true)}
      {amount('construction_cost', '建築工事費')}
      {amount('brokerage_fee_in', '仲介手数料（受取）')}
      {amount('brokerage_fee_out', '仲介手数料（支払）')}
      <Field label="仲介業者" value={
        <InlineCustomerField value={u.broker_customer_id} name={u.broker_name} label="仲介業者" corporateOnly onSave={(id) => save({ broker_customer_id: id }, '仲介業者')} />
      } />
      {u.plan_diff_months !== null && <Field label="予定との差" value={u.plan_diff_months === 0 ? '同月' : `${u.plan_diff_months > 0 ? '+' : ''}${u.plan_diff_months} か月`} />}
      {u.phase === LOST_PHASE && <Field label="失注理由" value={`${u.lost_reason_category || ''} ${u.lost_reason_detail || ''}`} />}
    </Box>
  );
}

function Field({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Box>
  );
}

// --------------------------------------------------
// 区画の登録・編集（顧客 複数・代表者）
// --------------------------------------------------
interface UnitForm {
  unit_name: string; unit_kind: string; expected_order_ym: string; order_confirmed_date: string; sales_amount: string;
  construction_cost: string; brokerage_fee_in: string; brokerage_fee_out: string; broker: CustomerOption | null;
  system_numbers: string[]; // 案件番号 (Hozenall NET で採番し工事管理へ引き継ぐ。複数可)
  customers: { option: CustomerOption; share: string; is_representative: boolean }[];
}

export function UnitDialog({ open, unit, projectId, flat, operatorId, onClose, onSaved }: {
  open: boolean; unit: SfaUnit | null; projectId: string; flat: boolean; operatorId?: string; onClose: () => void; onSaved: () => void;
}) {
  const notify = useNotification();
  const [f, setF] = useState<UnitForm>(empty());
  const [picker, setPicker] = useState<CustomerOption | null>(null);
  const [saving, setSaving] = useState(false);
  function empty(): UnitForm {
    return { unit_name: '', unit_kind: '建売', expected_order_ym: '', order_confirmed_date: '', sales_amount: '', construction_cost: '', brokerage_fee_in: '', brokerage_fee_out: '', broker: null, system_numbers: [], customers: [] };
  }
  useEffect(() => {
    if (!open) return;
    setPicker(null);
    setF(unit ? {
      unit_name: unit.unit_name || '', unit_kind: unit.unit_kind || '建売', expected_order_ym: unit.expected_order_ym || '', order_confirmed_date: unit.order_confirmed_date || '',
      sales_amount: unit.sales_amount?.toString() || '', construction_cost: unit.construction_cost?.toString() || '', brokerage_fee_in: unit.brokerage_fee_in?.toString() || '', brokerage_fee_out: unit.brokerage_fee_out?.toString() || '',
      broker: unit.broker_customer_id ? { id: unit.broker_customer_id, display_name: unit.broker_name || '', customer_type: 'corporate' } : null,
      system_numbers: unit.system_numbers || [],
      customers: unit.customers.map((c) => ({ option: { id: c.customer_id, display_name: c.display_name || '', customer_type: (c.customer_type as any) || 'individual' }, share: fmtShare(c.share_numerator, c.share_denominator), is_representative: !!c.is_representative })),
    } : empty());
  }, [open, unit]);
  const set = (k: keyof UnitForm, v: any) => setF((p) => ({ ...p, [k]: v }));

  const addCustomer = (o: CustomerOption | null) => {
    if (!o) return;
    if (f.customers.some((c) => c.option.id === o.id)) { notify.warning('同じ顧客が既にあります'); return; }
    set('customers', [...f.customers, { option: o, share: '', is_representative: f.customers.length === 0 }]);
    setPicker(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const customers: SfaUnitCustomer[] = [];
      for (const c of f.customers) {
        const share = parseShare(c.share);
        if (share === undefined) { notify.warning(`${c.option.display_name} の持分は「1/2」のように入力してください`); setSaving(false); return; }
        if (share && (share.num <= 0 || share.den <= 0 || share.num > share.den)) { notify.warning(`${c.option.display_name} の持分は 1 以上で分母以下の分数にしてください`); setSaving(false); return; }
        customers.push({ customer_id: c.option.id, share_numerator: share?.num ?? null, share_denominator: share?.den ?? null, is_representative: c.is_representative });
      }
      const body: any = {
        id: unit?.id, unit_name: f.unit_name, unit_kind: f.unit_kind, expected_order_ym: f.expected_order_ym, order_confirmed_date: f.order_confirmed_date,
        sales_amount: f.sales_amount, construction_cost: f.construction_cost, brokerage_fee_in: f.brokerage_fee_in, brokerage_fee_out: f.brokerage_fee_out,
        broker_customer_id: f.broker?.id || null, system_numbers: normalizeSystemNumbers(f.system_numbers), customers,
      };
      // 新規区画は販売情報を入れた時点で「1.見込み」。販売前の区画に情報を入れたときも見込みへ進める
      if (!unit) body.phase = '1.見込み';
      else if (unit.phase === IDLE_PHASE && (f.sales_amount || f.expected_order_ym || f.order_confirmed_date || customers.length)) body.phase = '1.見込み';
      await sfaStructureApi.upsertUnit(projectId, body, operatorId);
      notify.success(unit ? '更新しました' : '区画を追加しました');
      onSaved(); onClose();
    } catch (e: any) { notify.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableScrollLock>
      <DialogTitle sx={dialogTitleSx}><Icons.Home sx={{ fontSize: 18 }} />{unit ? (flat ? '販売情報の編集' : `販売区画の編集: ${unit.unit_name}`) : '販売区画の追加'}</DialogTitle>
      <DialogContent dividers>
        {saving ? <LoadingView message="保存中..." height="200px" size={40} /> : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, pt: 0.5 }}>
            {!flat && <TextField size="small" label="区画名" value={f.unit_name} onChange={(e) => set('unit_name', e.target.value)} placeholder="空欄なら A区画 B区画… を自動付番" />}
            <TextField select size="small" label="種別" value={f.unit_kind} onChange={(e) => set('unit_kind', e.target.value)}>
              {UNIT_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
            <AnkenNumberPicker value={f.system_numbers} links={unit?.anken_links} onChange={(v) => set('system_numbers', v)} />
            <TextField size="small" type="month" label="受注予定年月" value={f.expected_order_ym} onChange={(e) => set('expected_order_ym', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} helperText="確定後も上書きしない" />
            <TextField size="small" type="date" label="受注確定日" value={f.order_confirmed_date} onChange={(e) => set('order_confirmed_date', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} helperText="入力で計上月が確定" />
            <TextField size="small" label="売上（円）" value={f.sales_amount} onChange={(e) => set('sales_amount', e.target.value)} />
            <TextField size="small" label="建築工事費（円）" value={f.construction_cost} onChange={(e) => set('construction_cost', e.target.value)} />
            <TextField size="small" label="売却時仲介手数料 受取（円）" value={f.brokerage_fee_in} onChange={(e) => set('brokerage_fee_in', e.target.value)} />
            <TextField size="small" label="売却時仲介手数料 支払（円）" value={f.brokerage_fee_out} onChange={(e) => set('brokerage_fee_out', e.target.value)} />
            <Box sx={{ gridColumn: 'span 2' }}><CustomerPicker label="仲介業者（法人）" corporateOnly value={f.broker} onChange={(v) => set('broker', v)} /></Box>
            <Box sx={{ gridColumn: 'span 3' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>顧客（買主・施主など。複数可。代表者 1 人が案件の取引先として表示されます）</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>代表</TableCell><TableCell>顧客</TableCell><TableCell>持分（任意・何分の何）</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {f.customers.map((c, i) => (
                    <TableRow key={c.option.id}>
                      <TableCell><Radio size="small" checked={c.is_representative} onChange={() => set('customers', f.customers.map((x, j) => ({ ...x, is_representative: j === i })))} /></TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={c.option.customer_type === 'individual' ? 'success' : 'info'} label={c.option.customer_type === 'individual' ? '個人' : '法人'} sx={{ mr: 1 }} />{c.option.display_name}</TableCell>
                      <TableCell><TextField size="small" value={c.share} placeholder="例 1/2" onChange={(e) => set('customers', f.customers.map((x, j) => j === i ? { ...x, share: e.target.value } : x))} sx={{ width: 110 }} /></TableCell>
                      <TableCell><IconButton size="small" onClick={() => set('customers', f.customers.filter((_, j) => j !== i).map((x, j) => ({ ...x, is_representative: j === 0 && !f.customers.some((y, k) => k !== i && y.is_representative) ? true : x.is_representative })))}><Icons.Close fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ mt: 1 }}><CustomerPicker label="顧客を追加" value={picker} onChange={addCustomer} /></Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>キャンセル</Button>
        <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={saving}>保存する</Button>
      </DialogActions>
    </Dialog>
  );
}

// --------------------------------------------------
// 区画のフェーズ（売上フェーズ）。編集モードではチップの横に鉛筆が出て、浮いた入力欄で変える（ダイアログは開かない）。
// 選んだフェーズに必要な値の欄だけが増える（受注(6)は受注確定日、失注(7)は失注理由）。販売前（0.未着手）へは戻せない。
// 到達条件の検査と履歴の記録はバックエンド
// --------------------------------------------------
interface UnitPhaseDraft { phase: string; date: string; reason: string; detail: string }

export function InlineUnitPhaseField({ unit, projectId, operatorId, onChanged }: {
  unit: SfaUnit; projectId: string; operatorId?: string; onChanged: () => void | Promise<void>;
}) {
  const notify = useNotification();
  // 販売前 (0.未着手) は選択肢に無いため、未選択から始める
  const initial: UnitPhaseDraft = { phase: unit.phase === IDLE_PHASE ? '' : unit.phase, date: unit.order_confirmed_date || '', reason: '', detail: '' };
  return (
    <InlineEdit<UnitPhaseDraft>
      value={initial} label={`${unit.unit_name} のフェーズ`} editMinWidth={360}
      display={<UnitPhaseChip phase={unit.phase} />}
      isEqual={(a, b) => a.phase === b.phase}
      validate={(d) => (!d.phase ? 'フェーズを選んでください'
        : d.phase === ORDER_PHASE && !d.date ? '受注(6)にするときは受注確定日を入れてください'
          : d.phase === LOST_PHASE && !d.reason ? '失注理由を選んでください' : null)}
      onSave={async (d) => {
        try {
          await sfaStructureApi.changeUnitPhase(projectId, unit.id, {
            phase: d.phase, order_confirmed_date: d.date || undefined,
            lost_reason_category: d.phase === LOST_PHASE ? d.reason : undefined, lost_reason_detail: d.phase === LOST_PHASE ? d.detail || undefined : undefined,
          }, operatorId);
        } catch (e: any) {
          notify.error(e.message);
          throw e;
        }
        notify.success(`${unit.unit_name} のフェーズを「${d.phase}」に変更しました`);
        await onChanged();
      }}
      renderEditor={(args) => <UnitPhaseEditor {...args} />}
    />
  );
}

function UnitPhaseEditor({ draft, setDraft, saving, onKeyDown }: InlineEditorArgs<UnitPhaseDraft>) {
  const reasons = useLostReasons();
  const set = (patch: Partial<UnitPhaseDraft>) => setDraft({ ...draft, ...patch });
  const menu = { MenuProps: { disableScrollLock: true } };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 0.75 }}>
      <TextField select fullWidth size="small" label="新しいフェーズ" autoFocus value={draft.phase} disabled={saving}
        onChange={(e) => set({ phase: e.target.value })} slotProps={{ select: { defaultOpen: true, ...menu } }}>
        {SELECTABLE_PHASES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
      </TextField>
      {draft.phase === ORDER_PHASE && (
        <TextField fullWidth size="small" type="date" label="受注確定日（必須）" value={draft.date} disabled={saving}
          onChange={(e) => set({ date: e.target.value })} onKeyDown={onKeyDown} slotProps={{ inputLabel: { shrink: true } }} />
      )}
      {draft.phase === LOST_PHASE && (
        <>
          <TextField select fullWidth size="small" label="失注理由（必須）" value={draft.reason} disabled={saving}
            onChange={(e) => set({ reason: e.target.value })} slotProps={{ select: menu }}>
            {reasons.map((r) => <MenuItem key={r.reason_code} value={r.reason_code}>{r.reason_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth size="small" label="詳細" value={draft.detail} disabled={saving} multiline minRows={2}
            onChange={(e) => set({ detail: e.target.value })} />
        </>
      )}
      <Typography variant="caption" color="text.secondary">
        販売を始めた区画は販売前（0.未着手）へは戻せません。変更は履歴に記録されます。
      </Typography>
    </Box>
  );
}
